/**
 * O envio de um diálogo de item: trava durante a gravação e, se o servidor recusar, põe a mensagem
 * junto do campo que ela cita (ou no topo do diálogo). O que foi digitado nunca é tocado.
 */
import { useCallback, useRef, useState } from 'react';
import { errorMessage, fieldOfServerMessage } from '../wizardValidation';

export type ItemErrors<F extends string> = Partial<Record<F, string>>;

export function useItemSubmit<F extends string>(
  labels: ReadonlyArray<[string, F]>,
  fallback: string
) {
  // Trava síncrona (ref): dois envios no mesmo tick leem `submitting` antigo e gravariam o item duas vezes.
  const sending = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<ItemErrors<F>>({});

  /** `true` quando gravou; `false` quando algo ficou a corrigir (e o diálogo continua aberto). */
  const run = useCallback(
    async (save: () => Promise<void>): Promise<boolean> => {
      if (sending.current) return false;
      sending.current = true;
      setSubmitting(true);
      setError(null);
      try {
        await save();
        return true;
      } catch (err: unknown) {
        const message = errorMessage(err, fallback);
        const field = fieldOfServerMessage(message, labels);
        if (field) setErrors({ [field]: message } as ItemErrors<F>);
        else setError(message);
        return false;
      } finally {
        sending.current = false;
        setSubmitting(false);
      }
    },
    [labels, fallback]
  );

  return { submitting, error, errors, setErrors, run };
}
